"use client";

import * as React from "react";
import { useStore } from "@/lib/store";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { VocabularyList } from "@/components/vocabulary-list";
import { useTextToSpeech } from "@/hooks/use-text-to-speech";
import { useToast } from "@/hooks/use-toast";
import {
  Volume2,
  VolumeX,
  Square,
  Save,
  Trash2,
  Copy,
  Check,
  BookOpen,
  Languages,
  Sparkles,
  Type,
  AlignLeft,
  Pilcrow,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { SelectionMode } from "@/lib/store";

type DefineResponse = {
  word: string;
  raw: string;
  partOfSpeech: string;
  pronunciation: string;
  translation: string;
  definition: string;
  example: string;
  synonyms: string;
};

type TranslateResponse = {
  translation: string;
};

export function SelectionPanel() {
  const parsed = useStore((s) => s.parsed);
  const selectionMode = useStore((s) => s.selectionMode);
  const selectedWordId = useStore((s) => s.selectedWordId);
  const selectedSentenceId = useStore((s) => s.selectedSentenceId);
  const selectedParagraphId = useStore((s) => s.selectedParagraphId);
  const setSelectionMode = useStore((s) => s.setSelectionMode);
  const moveSelection = useStore((s) => s.moveSelection);
  const addToVocabulary = useStore((s) => s.addToVocabulary);
  const inVocabulary = useStore((s) => s.inVocabulary);
  const vocabulary = useStore((s) => s.vocabulary);
  const { toast } = useToast();
  const { speak, stop, speaking, supported: ttsSupported } = useTextToSpeech();

  const [rightTab, setRightTab] = React.useState<"selection" | "vocabulary">("selection");
  const [defLoading, setDefLoading] = React.useState(false);
  const [defError, setDefError] = React.useState<string | null>(null);
  const [definition, setDefinition] = React.useState<DefineResponse | null>(null);
  const [trLoading, setTrLoading] = React.useState(false);
  const [trError, setTrError] = React.useState<string | null>(null);
  const [translation, setTranslation] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);
  const [spokenOnce, setSpokenOnce] = React.useState<string | null>(null);

  // Derive the currently selected text
  const selection = React.useMemo(() => {
    if (!parsed) return null;
    if (selectionMode === "word" && selectedWordId) {
      const w = parsed.words.find((x) => x.id === selectedWordId);
      if (!w) return null;
      const sentence = parsed.sentences.find((s) => s.id === w.sentenceId);
      return {
        mode: "word" as const,
        word: w.text,
        sentenceText: sentence?.text ?? "",
        fullText: w.text,
        context: sentence?.text ?? "",
        wordId: w.id,
      };
    }
    if (selectionMode === "phrase" && selectedSentenceId) {
      const s = parsed.sentences.find((x) => x.id === selectedSentenceId);
      if (!s) return null;
      const w = s.wordIds
        .map((id) => parsed.words.find((x) => x.id === id))
        .find((x) => x && x.id === selectedWordId);
      return {
        mode: "phrase" as const,
        word: w?.text ?? "",
        sentenceText: s.text,
        fullText: s.text,
        context: s.text,
        wordId: w?.id ?? null,
      };
    }
    if (selectionMode === "paragraph" && selectedParagraphId) {
      const p = parsed.paragraphs.find((x) => x.id === selectedParagraphId);
      if (!p) return null;
      return {
        mode: "paragraph" as const,
        word: "",
        sentenceText: "",
        fullText: p.text,
        context: p.text,
        wordId: null,
      };
    }
    return null;
  }, [parsed, selectionMode, selectedWordId, selectedSentenceId, selectedParagraphId]);

  // Reset cached AI results when selection changes
  React.useEffect(() => {
    setDefinition(null);
    setDefError(null);
    setTranslation(null);
    setTrError(null);
    setCopied(false);
    setSpokenOnce(null);
  }, [selection?.wordId, selection?.mode, selection?.fullText]);

  // Switch to "selection" tab when a new selection is made
  React.useEffect(() => {
    if (selection) setRightTab("selection");
  }, [selection?.wordId, selection?.mode]);

  // Listen for "show vocabulary" requests from the header button
  React.useEffect(() => {
    const handler = () => setRightTab("vocabulary");
    window.addEventListener("rf:show-vocabulary", handler);
    return () => window.removeEventListener("rf:show-vocabulary", handler);
  }, []);

  // Auto-fetch translation for the current selection (debounced)
  React.useEffect(() => {
    if (!selection) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setTrLoading(true);
      setTrError(null);
      try {
        const res = await fetch("/api/translate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: selection.fullText, mode: selection.mode }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d?.error || `HTTP ${res.status}`);
        }
        const data = (await res.json()) as TranslateResponse;
        if (!cancelled) {
          setTranslation(data.translation);
        }
      } catch (err) {
        if (!cancelled) {
          setTrError(err instanceof Error ? err.message : "Falha na tradução.");
        }
      } finally {
        if (!cancelled) setTrLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [selection?.fullText, selection?.mode]);

  // Auto-fetch definition when in WORD mode
  React.useEffect(() => {
    if (!selection || selection.mode !== "word" || !selection.word) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setDefLoading(true);
      setDefError(null);
      try {
        const res = await fetch("/api/define", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ word: selection.word, context: selection.context }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d?.error || `HTTP ${res.status}`);
        }
        const data = (await res.json()) as DefineResponse;
        if (!cancelled) setDefinition(data);
      } catch (err) {
        if (!cancelled) {
          setDefError(err instanceof Error ? err.message : "Falha ao buscar definição.");
        }
      } finally {
        if (!cancelled) setDefLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [selection?.word, selection?.mode, selection?.context]);

  function handleSpeak() {
    if (!selection) return;
    if (speaking) {
      stop();
      return;
    }
    speak(selection.fullText);
    setSpokenOnce(selection.fullText);
  }

  function handleSaveWord() {
    if (!selection || selection.mode !== "word" || !selection.word) {
      toast({
        title: "Selecione uma palavra",
        description: "Volte para o modo palavra (Tab) para salvá-la no vocabulário.",
      });
      return;
    }
    if (inVocabulary(selection.word)) {
      toast({
        title: "Já está no vocabulário",
        description: `"${selection.word}" já foi salva antes.`,
      });
      return;
    }
    addToVocabulary({
      word: selection.word,
      context: selection.context,
      translation: definition?.translation ?? translation,
      definition: definition?.definition ?? null,
    });
    toast({
      title: "Palavra salva!",
      description: `"${selection.word}" foi adicionada ao seu vocabulário.`,
    });
  }

  async function handleCopy() {
    if (!selection) return;
    try {
      await navigator.clipboard.writeText(selection.fullText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }

  if (!parsed || !selection) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
          <BookOpen className="h-7 w-7 text-muted-foreground" />
        </div>
        <h3 className="font-medium mb-1">Clique em uma palavra</h3>
        <p className="text-sm text-muted-foreground max-w-xs">
          Clique em qualquer palavra do texto para começar. Use Tab para alternar entre palavra,
          frase e parágrafo.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="shrink-0 border-b px-3 pt-3">
        <Tabs value={rightTab} onValueChange={(v) => setRightTab(v as "selection" | "vocabulary")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="selection" className="gap-2 text-xs">
              <Sparkles className="h-3.5 w-3.5" />
              Seleção
            </TabsTrigger>
            <TabsTrigger value="vocabulary" className="gap-2 text-xs relative">
              <BookOpen className="h-3.5 w-3.5" />
              Vocabulário
              {vocabulary.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">
                  {vocabulary.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="selection" className="mt-0" />
          <TabsContent value="vocabulary" className="mt-0" />
        </Tabs>
      </div>

      {rightTab === "selection" ? (
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            {/* Mode switcher */}
            <div className="flex items-center gap-1.5">
              <ModeButton
                active={selectionMode === "word"}
                onClick={() => setSelectionMode("word")}
                icon={<Type className="h-3.5 w-3.5" />}
                label="Palavra"
                hint="Tab"
              />
              <ModeButton
                active={selectionMode === "phrase"}
                onClick={() => setSelectionMode("phrase")}
                icon={<AlignLeft className="h-3.5 w-3.5" />}
                label="Frase"
                hint="Tab"
              />
              <ModeButton
                active={selectionMode === "paragraph"}
                onClick={() => setSelectionMode("paragraph")}
                icon={<Pilcrow className="h-3.5 w-3.5" />}
                label="Parágrafo"
                hint="Tab"
              />
            </div>

            {/* Selected text display */}
            <Card className="p-4 bg-muted/30 border-dashed">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium">
                  {selection.mode === "word"
                    ? "Palavra selecionada"
                    : selection.mode === "phrase"
                    ? "Frase selecionada"
                    : "Parágrafo selecionado"}
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => moveSelection("prev")}
                    title="Anterior (←)"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    onClick={() => moveSelection("next")}
                    title="Próximo (→)"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div
                className={cn(
                  "leading-relaxed",
                  selection.mode === "word" && "text-3xl font-semibold tracking-tight",
                  selection.mode === "phrase" && "text-lg",
                  selection.mode === "paragraph" && "text-sm"
                )}
              >
                {selection.mode === "word"
                  ? selection.word
                  : selection.mode === "phrase"
                  ? selection.fullText
                  : selection.fullText}
              </div>
              {selection.mode === "word" && selection.context && (
                <div className="mt-3 pt-3 border-t border-dashed text-xs text-muted-foreground italic">
                  &ldquo;{selection.context}&rdquo;
                </div>
              )}
            </Card>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleSpeak}
                variant={speaking ? "destructive" : "default"}
                size="sm"
                className="gap-2"
                disabled={!ttsSupported}
                title={ttsSupported ? "Falar (T)" : "TTS não suportado neste navegador"}
              >
                {speaking ? (
                  <>
                    <Square className="h-3.5 w-3.5" />
                    Parar
                  </>
                ) : (
                  <>
                    <Volume2 className="h-3.5 w-3.5" />
                    Ouvir
                  </>
                )}
              </Button>
              {selection.mode === "word" && (
                <Button
                  onClick={handleSaveWord}
                  variant="secondary"
                  size="sm"
                  className="gap-2"
                  disabled={inVocabulary(selection.word)}
                  title="Salvar no vocabulário (S)"
                  data-save-word-btn="true"
                >
                  <Save className="h-3.5 w-3.5" />
                  {inVocabulary(selection.word) ? "Salva" : "Salvar"}
                </Button>
              )}
              <Button
                onClick={handleCopy}
                variant="outline"
                size="sm"
                className="gap-2"
                title="Copiar texto"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5" />
                    Copiado
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Copiar
                  </>
                )}
              </Button>
            </div>

            <Separator />

            {/* Translation */}
            <section>
              <div className="flex items-center gap-2 mb-2">
                <Languages className="h-4 w-4 text-muted-foreground" />
                <h4 className="text-sm font-medium">Tradução (PT-BR)</h4>
              </div>
              {trLoading ? (
                <Skeleton className="h-12 w-full" />
              ) : trError ? (
                <p className="text-xs text-destructive">{trError}</p>
              ) : (
                <p className="text-sm leading-relaxed">{translation ?? "—"}</p>
              )}
            </section>

            {/* Definition (word mode only) */}
            {selection.mode === "word" && (
              <>
                <Separator />
                <section>
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-medium">Definição</h4>
                  </div>
                  {defLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  ) : defError ? (
                    <p className="text-xs text-destructive">{defError}</p>
                  ) : definition ? (
                    <div className="space-y-3 text-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        {definition.partOfSpeech && (
                          <Badge variant="outline" className="text-[10px] capitalize">
                            {definition.partOfSpeech}
                          </Badge>
                        )}
                        {definition.pronunciation && (
                          <span className="text-xs font-mono text-muted-foreground">
                            {definition.pronunciation}
                          </span>
                        )}
                      </div>
                      {definition.translation && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
                            Tradução
                          </div>
                          <div className="font-medium">{definition.translation}</div>
                        </div>
                      )}
                      {definition.definition && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
                            Definição
                          </div>
                          <div className="text-muted-foreground leading-relaxed">
                            {definition.definition}
                          </div>
                        </div>
                      )}
                      {definition.example && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
                            Exemplo
                          </div>
                          <div className="italic text-muted-foreground">
                            &ldquo;{definition.example}&rdquo;
                          </div>
                        </div>
                      )}
                      {definition.synonyms && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">
                            Sinônimos
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {definition.synonyms
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean)
                              .map((s, i) => (
                                <Badge key={i} variant="secondary" className="text-[10px]">
                                  {s}
                                </Badge>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">—</p>
                  )}
                </section>
              </>
            )}

            {/* Speaking indicator */}
            {speaking && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground rf-pulse">
                <Volume2 className="h-3 w-3" />
                Reproduzindo áudio...
                <Button size="sm" variant="ghost" className="h-6 px-2" onClick={stop}>
                  <VolumeX className="h-3 w-3 mr-1" /> Parar
                </Button>
              </div>
            )}

            {spokenOnce && !speaking && (
              <p className="text-[10px] text-muted-foreground">
                Última reprodução: &ldquo;{spokenOnce.length > 60
                  ? spokenOnce.slice(0, 60) + "…"
                  : spokenOnce}
                &rdquo;
              </p>
            )}
          </div>
        </ScrollArea>
      ) : (
        <VocabularyList />
      )}
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  hint?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex-1 flex flex-col items-center gap-1 py-2 rounded-md border text-xs transition-colors",
        active
          ? "bg-primary text-primary-foreground border-primary"
          : "bg-background border-border hover:bg-muted/50 text-muted-foreground"
      )}
      title={hint ? `${label} (${hint})` : label}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
